import {noop} from '@github-ui/noop'
import {testIdProps} from '@github-ui/test-id-props'
import {ActionList, ActionMenu} from '@primer/react'
import {useCallback} from 'react'
import {flushSync} from 'react-dom'

import {TableRowHeight} from '../../api/view/contracts'
import {ROW_HEIGHT_LABELS} from '../../helpers/table-row-height'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {useMemexProjectViewRootHeight} from '../../hooks/use-memex-app-root-height'
import {useTableRowHeight} from '../../hooks/use-table-row-height'
import {useViews} from '../../hooks/use-views'

type MenuProps = {
  open: boolean
  setOpen: (open: boolean) => void
  anchorRef: React.RefObject<HTMLElement | null>
  id?: string
}

const ROW_HEIGHT_OPTIONS = [
  TableRowHeight.Single,
  TableRowHeight.Medium,
  TableRowHeight.Tall,
  TableRowHeight.ExtraTall,
] as const

export function RowHeightMenu({id, open, setOpen, anchorRef}: MenuProps) {
  const {clientHeight} = useMemexProjectViewRootHeight({
    onResize: () => {
      if (open) {
        // eslint-disable-next-line @eslint-react/dom-no-flush-sync
        flushSync(() => {
          setOpen(false)
        })
        setOpen(true)
      }
    },
  })

  return (
    <ActionMenu open={open} anchorRef={anchorRef} onOpenChange={noop}>
      <ActionMenu.Overlay
        style={{maxHeight: clientHeight}}
        {...testIdProps('row-height-menu')}
        role="dialog"
        aria-label="Select row height"
        onEscape={() => setOpen(false)}
        onClickOutside={() => setOpen(false)}
      >
        <Options id={id} setOpen={setOpen} />
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function Options({id, setOpen}: Pick<MenuProps, 'id' | 'setOpen'>) {
  const {hasWritePermissions} = ViewerPrivileges()
  const {currentView} = useViews()
  const {rowHeight, setRowHeight} = useTableRowHeight()

  const handleSelect = useCallback(
    (height: TableRowHeight) => {
      if (!currentView || !hasWritePermissions) return

      setRowHeight(currentView.number, height)
      setOpen(false)
    },
    [currentView, hasWritePermissions, setOpen, setRowHeight],
  )

  return (
    <ActionList id={id} selectionVariant="single">
      <ActionList.Group>
        <ActionList.GroupHeading>Row height</ActionList.GroupHeading>
        {ROW_HEIGHT_OPTIONS.map(option => (
          <ActionList.Item
            key={option}
            selected={rowHeight === option}
            disabled={!hasWritePermissions}
            onSelect={() => handleSelect(option)}
            {...testIdProps(`row-height-${option}`)}
          >
            {ROW_HEIGHT_LABELS[option]}
          </ActionList.Item>
        ))}
      </ActionList.Group>
    </ActionList>
  )
}
